import { useCallback, useState } from 'react';

/**
 * Lightweight step state management for import modals.
 * Provides forward/back navigation through an ordered list of steps.
 */
export default function useImportSteps<S extends string>(steps: readonly S[]) {
  const [currentStep, setCurrentStep] = useState<S>(steps[0]);

  const currentIndex = steps.indexOf(currentStep);
  const canGoBack = currentIndex > 0;
  const canGoNext = currentIndex < steps.length - 1;

  const goTo = useCallback(
    (step: S) => {
      if (steps.includes(step)) {
        setCurrentStep(step);
      }
    },
    [steps],
  );

  const goNext = useCallback(() => {
    const idx = steps.indexOf(currentStep);
    if (idx < steps.length - 1) {
      setCurrentStep(steps[idx + 1]);
    }
  }, [steps, currentStep]);

  const goBack = useCallback(() => {
    const idx = steps.indexOf(currentStep);
    if (idx > 0) {
      setCurrentStep(steps[idx - 1]);
    }
  }, [steps, currentStep]);

  const isStep = useCallback((step: S) => currentStep === step, [currentStep]);

  const reset = useCallback(() => {
    setCurrentStep(steps[0]);
  }, [steps]);

  return {
    currentStep,
    currentIndex,
    stepCount: steps.length,
    canGoBack,
    canGoNext,
    goTo,
    goNext,
    goBack,
    isStep,
    reset,
  } as const;
}
